import type {Iso8601Date, Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export enum DatasetType {
    pointInTime = "43e5174c-32db-47d5-8223-23916fe01e8e",
    periodic = "1bf49553-e3f5-49ca-84fc-bb2d869a68c7",
    // lifetime is a type of nonTimeSeries, so not including it here for now
    nonTimeSeries = "0ac1dc64-bdad-45ce-af5e-ccbf695e421f",
}

export enum TimeGranularity {
    // secondly? = "1762baec-08a9-49ff-ac55-71532cd8efba",
    // minutely? = "6475a702-e6c8-4c7d-8205-0a0f8a2502cc",
    hourly = "16664538-4bde-4ad9-9b7f-1908ac821a3c",
    daily = "162da7f3-968e-4120-bbb4-5e332e50430f",
    weekly = "fd99e095-9292-444c-b770-aba6f9dbb733",
    monthly = "1c340455-3042-40d2-ac1f-6cb2cdf1a1f8",
    yearly = "43be85bb-ed34-4fb8-ae34-0197c6e10c25",
}

export enum MetricAggregationFunction {
    sum = "203843b7-683a-4b49-96b9-b77bad0527a5",
    count = "c64ad99c-5209-48ef-8f1f-f3b625fbbdf7",
    minimum = "bc5115b8-ba46-4626-aa80-d5bdb989abb2",
    maximum = "b30eabf2-3685-4c22-864f-8b66b3b06691",
    average = "0e94582f-26b5-46c9-b60d-8f5026fce445",
}

export enum DatasetColumnRole {
    // id = "12769a9f-9fd9-4449-bd34-397df69a48ba",
    // time = "f6e261bd-12c6-400c-bf62-8d1116eb4c49",
    metric = "124699bb-806c-4c6f-9b39-5a25a9180b30",
    dimension = "ea585df2-051d-494a-88cc-01e12d93e21c",
}

export enum DatasetColumnDataType {
    string = "8dca9e43-8567-4bc1-84ad-42c35132a654",
    // integer = "3d5b6a95-ff72-46bd-bf0e-aa2b6e8f98bb",
    number = "6975d440-0533-4928-bcc4-1236251bec60",
    // time = "413dda74-93f9-4b55-8ce9-2e8adbfe7fa6",
    date = "0dcdfae9-2db2-46ba-aefc-f17917fff33e",
    dateTime = "82242498-a2bf-455f-ab17-c3525fb4cd15",
}

export type DatasetColumn = {
    id: Uuid;
    displayName: string;
    role: DatasetColumnRole;
    dataType: DatasetColumnDataType;
    resolutionDetails: unknown;
};

export type Dataset = {
    id: Uuid;
    displayName: string;
    columns: Array<DatasetColumn>;
    // TODO: Can timeZoneId be here ??
    // timeZoneId: Uuid | null;
} & (
    | {
          type: DatasetType.pointInTime;
      }
    | {
          type: DatasetType.periodic;
          granularity: TimeGranularity;
      }
    | {
          type: DatasetType.nonTimeSeries;
      }
);

export enum FilterType {
    // aggregate filters
    and = "a22e3a47-e39c-4ddd-a87b-419a2e772fd9",
    or = "b87e8be9-3848-4f54-ab4c-0e9c6e745ba4",
    // atomic filters
    stringEquals = "c3b97143-ceff-41cc-be16-798208de5a0d",
    stringBeginsWith = "15de09b9-8fb7-4d5f-a978-8d161b1195ec",
    stringEndsWith = "193f6b08-bd38-4389-854f-72840c49a881",
    stringContains = "6533ba29-6d09-425e-874c-3846adbdc5d1",
    stringIn = "231e3a8c-4507-43b5-b3cf-8b5ed59f5aa3",
    numberEquals = "8a4c42eb-aca8-47f0-bf3c-2ed9d268ffd5",
    numberLessThan = "39d3b9b0-28db-4e6c-9bfc-d10b3ee4dd84",
    numberLessThanEqualTo = "faabb5e3-a6ae-4c1a-8c3d-76c2b1de3fae",
    numberGreaterThan = "e97b4e3e-3d42-4f88-a50d-51263df1bcdd",
    numberGreaterThanEqualTo = "3e7f5b2a-9df2-4d8e-bc39-74b1a23a51db",
    dateEquals = "ab9f6c2d-9f6c-40b0-b0b5-d09e7138da1f",
    dateLessThan = "0b09e6a1-52e4-4dba-a1d4-5c768cfe6d3b",
    dateGreaterThanEqualTo = "8b76054c-5f8d-4978-b01a-d022208068cb",
    dateBetween = "ff4392aa-81ea-4ccd-b385-69860c202203",
    dateTimeEquals = "0822274b-82ec-4768-8d3f-418123670a4a",
    dateTimeLessThan = "acb292c2-edaa-4b4e-bfbd-6278575d04fc",
    dateTimeLessThanEqualTo = "f1e30e72-af4c-4b3a-b85f-987790a551b8",
    dateTimeGreaterThan = "b149b7db-a07f-4152-b775-ccf0f1b5db5f",
    dateTimeGreaterThanEqualTo = "0cc04a6e-daab-4358-bd86-b631cf693922",
    dateTimeBetween = "eff746e2-c996-4b20-bda9-c2d254978075",
}

export type Filter = AggregateFilter | AtomicFilter;

type AggregateFilter =
    | {
          type: FilterType.and;
          predicates: Array<Filter>;
      }
    | {
          type: FilterType.or;
          predicates: Array<Filter>;
      };

export type AtomicFilter =
    | {
          type: FilterType.stringEquals;
          attribute: Uuid;
          value: string;
      }
    | {
          type: FilterType.stringBeginsWith;
          attribute: Uuid;
          value: string;
      }
    | {
          type: FilterType.stringEndsWith;
          attribute: Uuid;
          value: string;
      }
    | {
          type: FilterType.stringContains;
          attribute: Uuid;
          value: string;
      }
    | {
          type: FilterType.stringIn;
          attribute: Uuid;
          values: Array<string>;
      }
    | {
          type: FilterType.numberEquals;
          attribute: Uuid;
          value: number;
      }
    | {
          type: FilterType.numberLessThan;
          attribute: Uuid;
          value: number;
      }
    | {
          type: FilterType.numberLessThanEqualTo;
          attribute: Uuid;
          value: number;
      }
    | {
          type: FilterType.numberGreaterThan;
          attribute: Uuid;
          value: number;
      }
    | {
          type: FilterType.numberGreaterThanEqualTo;
          attribute: Uuid;
          value: number;
      }
    | {
          type: FilterType.dateEquals;
          attribute: Uuid;
          value: Iso8601Date;
      }
    | {
          type: FilterType.dateLessThan;
          attribute: Uuid;
          value: Iso8601Date;
      }
    | {
          type: FilterType.dateGreaterThanEqualTo;
          attribute: Uuid;
          value: Iso8601Date;
      }
    | {
          type: FilterType.dateBetween;
          attribute: Uuid;
          from: Iso8601Date;
          to: Iso8601Date;
      }
    | {
          type: FilterType.dateTimeEquals;
          attribute: Uuid;
          value: Iso8601DateTime;
      }
    | {
          type: FilterType.dateTimeLessThan;
          attribute: Uuid;
          value: Iso8601DateTime;
      }
    | {
          type: FilterType.dateTimeLessThanEqualTo;
          attribute: Uuid;
          value: Iso8601DateTime;
      }
    | {
          type: FilterType.dateTimeGreaterThan;
          attribute: Uuid;
          value: Iso8601DateTime;
      }
    | {
          type: FilterType.dateTimeGreaterThanEqualTo;
          attribute: Uuid;
          value: Iso8601DateTime;
      }
    | {
          type: FilterType.dateTimeBetween;
          attribute: Uuid;
          from: Iso8601DateTime;
          to: Iso8601DateTime;
      };

export enum SortType {
    ascending = "cc2d3fec-2848-4a44-aa83-a969090fa32a",
    descending = "c7f82083-10b0-41ab-b5ea-1e4f462e6586",
}

export type SortLineItem = {
    id: Uuid;
    sortType: SortType;
};

export type SortConfiguration = Array<SortLineItem>;
